import { tableConfig, uiConfig } from '../../config/appConfig';
import {
  APPLICATION_SETTING_CATEGORY_LABELS,
  formatApplicationSettingCategory,
} from './applicationSettingCategories';

export const applicationSettingConfig = {
  entityName: 'ApplicationSetting',

  // Backend API Configuration
  api: {
    baseEndpoint: '/application-setting',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      getByKey: (key) => `/key/${key}`
    },
    lookupEndpoints: {
      // No lookups needed for application settings
    },
    // SWR invalidation endpoints
    swrInvalidation: {
      mainList: '/application-setting',
    }
  },

  // UI Configuration
  ui: {
    drawerWidth: uiConfig.defaultDrawerWidth,
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    tableVariant: 'categoryGroups',
    categoryGroupField: 'category',
    formatCategoryLabel: formatApplicationSettingCategory,
  },

  // Service configuration
  service: {
    name: 'applicationSettingService',
    methods: {
      getAll: 'getAllApplicationSettings',
      getById: 'getApplicationSettingById',
      create: 'createApplicationSetting',
      update: 'updateApplicationSetting',
      delete: 'deleteApplicationSetting',
      validate: 'validateData'
    }
  },

  // Display Configuration
  displayName: 'Application Settings',
  description: 'Manage application-wide settings and variables, including company logo for reports',
  icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
  
  // Features configuration
  features: {
    fileAttachments: false,
    masterEntityTypeId: null,
  },

  // Fields to remove from forms before submission
  fieldsToRemove: [
    'id', 'updatedBy', 'createdAt', 'updatedAt', 'fileAttachmentCount', 'fileAttachments'
  ],

  // UNIFIED FIELD CONFIGURATION
  fields: [
    {
      name: 'id',
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'key',
      label: 'Key',
      type: 'text',
      required: true,
      placeholder: 'e.g., companyLogo, companyName',
      table: {
        header: 'Key',
        width: '180px',
        sortable: true
      },
      filterable: true,
      validation: {
        required: true,
        minLength: 1,
        maxLength: 100,
        pattern: '^[a-zA-Z0-9_.]+$',
        patternMessage: 'Key must contain only letters, numbers, underscores, and dots'
      },
      helpText: 'Unique identifier for this setting (e.g., companyLogo)'
    },
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      required: false,
      placeholder: 'Select category',
      options: Object.entries(APPLICATION_SETTING_CATEGORY_LABELS).map(([value, label]) => ({
        value,
        label,
      })),
      table: false,
      filterable: true,
      validation: {
        maxLength: 100
      },
      helpText: 'Determines which accordion section shows this setting on the list page'
    },
    {
      name: 'value',
      label: 'Value',
      type: 'textarea',
      required: false,
      placeholder: 'Enter the setting value (file path, URL, or text)',
      rows: 3,
      table: {
        header: 'Value',
        width: '250px',
        sortable: true,
        cellRenderer: 'truncate',
        truncateLength: 40
      },
      filterable: true,
      validation: {
        maxLength: 2000
      },
      helpText: 'The value for this setting. For logos, use file path or URL.'
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      placeholder: 'Describe what this setting is used for',
      rows: 2,
      table: {
        header: 'Description',
        width: '250px',
        sortable: true,
        cellRenderer: 'truncate',
        truncateLength: 50
      },
      filterable: true,
      validation: {
        maxLength: 500
      }
    },
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      defaultValue: true,
      switchLabel: 'Active',
      labelPosition: 'left',
      table: {
        header: 'Status',
        width: '100px',
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true
    },
    {
      name: 'updatedBy',
      label: 'Updated By',
      type: 'relation',
      relationType: 'user',
      table: false, // Hide from table by default
      filterable: false,
      form: false
    },
    {
      name: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      table: false, // Hide from table by default
      filterable: false,
      form: false
    },
    {
      name: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      table: {
        header: 'Last Updated',
        width: '150px',
        sortable: true,
        cellRenderer: 'dateTime'
      },
      filterable: false,
      form: false
    }
  ],

  // Default form values
  defaultFormValues: {
    isActive: true,
    category: 'general'
  },

  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    if (!this.fields || !Array.isArray(this.fields)) {
      console.warn('⚠️ applicationSettingConfig.fields is not available or not an array');
      return [];
    }
    
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name,
        truncateLength: field.table.truncateLength,
        maxLines: field.table.maxLines,
        maxLength: field.table.maxLength
      }));
    
    return columns;
  },

  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  exportFileName: 'qms_application_settings',

  // Backend field mappings (if needed for nested display fields)
  fieldMappings: {
    updatedBy: 'updatedBy.userDisplayName'
  },

  // Table configuration
  table: {
    defaultSort: { field: 'category', direction: 'asc' },
    defaultFilters: {},
    stickyColumns: ['key'],
    rowActions: ['edit', 'delete'],
    bulkActions: ['delete']
  },

  // Form configuration
  form: {
    layout: 'vertical',
    showHelpText: true,
    groupFields: [
      {
        title: 'Basic Information',
        fields: ['key', 'value', 'description']
      },
      {
        title: 'Configuration',
        fields: ['category', 'isActive']
      }
    ]
  },

  // Labels and Text Configuration
  labels: {
    singular: 'Application Setting',
    plural: 'Application Settings',
    pageTitle: 'Application Settings',
    pageSubtitle: 'Use All or a category tab to browse and edit application settings.',
    searchPlaceholder: 'Search settings by key, value, or description...',
    viewTitle: 'View Application Setting',
    noSelectionText: 'Select a setting to see details'
  },

  // Help text for field descriptions
  helpText: {
    key: { short: 'Unique identifier for this setting (e.g., companyLogo). Must contain only letters, numbers, and underscores.' },
    value: { short: 'The value for this setting. For logos, use file path or URL. Can be any text value up to 2000 characters.' },
    description: { short: 'Describe what this setting is used for and where it is displayed in the application.' },
    category: { short: 'Group settings by category (e.g., company, reports, general) for better organization.' },
    isActive: { short: 'Enable or disable this setting. Inactive settings are not used by the application.' }
  },

  // Data transformation function for preparing data before API submission
  transformData: (data, formMode = 'add') => {
    const transformed = { ...data };
    
    // Remove fields that should not be sent to the backend
    const fieldsToRemove = [
      'id', 'updatedBy', 'createdAt', 'updatedAt', 
      'fileAttachmentCount', 'fileAttachments',
      'createdById', 'updatedById'
    ];
    
    fieldsToRemove.forEach(field => {
      if (transformed.hasOwnProperty(field)) {
        delete transformed[field];
      }
    });
    
    return transformed;
  }
};

export default applicationSettingConfig;

